import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import RecordPaymentModal from "@/components/accounts/RecordPaymentModal";
import ProjectPaymentSlideOver from "@/components/accounts/ProjectPaymentSlideOver";
import ProjectExpensesTab from "@/components/accounts/ProjectExpensesTab";
import StudioOverheadTab from "@/components/accounts/StudioOverheadTab";
import { useIsMobile } from "@/hooks/useIsMobile";

const TABS = [
  { key: "all", label: "All projects" },
  { key: "overdue", label: "Overdue" },
  { key: "recent", label: "Recent payments" },
  { key: "expenses", label: "Project expenses" },
  { key: "overhead", label: "Studio overhead" },
];

const TITLE_LABEL = { advance: "Advance", mid: "Mid", balance: "Balance", extra: "Extra" };

// Phase 7.0 Screen I — shared grid template across header + every project row.
const ROW_GRID = "2fr 120px 110px 110px 110px 1fr 170px";

export default function Accounts() {
  const { user, can, loading: authLoading } = useAuth();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("all");
  const [projects, setProjects] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recordTarget, setRecordTarget] = useState(null);
  const [slideOverId, setSlideOverId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    const params = {};
    if (tab === "overdue") params.overdue_only = true;
    if (search.trim()) params.q = search.trim();
    if (statusFilter) params.status = statusFilter;
    const [pRes, kRes] = await Promise.all([
      api.get("/accounts/projects", { params }),
      api.get("/accounts/kpis"),
    ]);
    setProjects(pRes.data.items || []);
    setKpis(kRes.data);
    if (tab === "recent") {
      const tRes = await api.get("/transactions");
      setRecentTxns(tRes.data.items || []);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [tab, statusFilter]);
  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => refresh(), 300);
    return () => clearTimeout(t);
  }, [search]);

  const onRecorded = () => { setRecordTarget(null); refresh(); };
  const onSlideChanged = () => refresh();

  if (authLoading) return null;
  if (user && !can("payments_write")) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="fade-up" data-testid="accounts-page">
        {/* Header */}
        <div style={{ marginBottom: isMobile ? 20 : 32 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#B08D55",
              marginBottom: 8,
            }}
          >
            PRODUCTION · ACCOUNTS
          </div>
          <h1
            className="font-display"
            style={{
              fontSize: isMobile ? "clamp(30px, 8.5vw, 52px)" : 52,
              lineHeight: 1.05,
              color: "#22382C",
            }}
            data-testid="accounts-title"
          >
            Accounts
          </h1>
          <p style={{ fontSize: 14, color: "#76705F", marginTop: 6 }}>
            Track payments, expenses, and project P&amp;L.
          </p>
        </div>

        {/* KPI strip — Mobile (Pass 2D): Total Receivable + Overdue in a 2-col
            row, Cash Collected full-width below. Desktop: 3-col row unchanged. */}
        <div
          data-testid="accounts-kpis"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
            gap: isMobile ? 10 : 20,
            marginBottom: isMobile ? 20 : 28,
          }}
        >
          <Kpi label="Total receivable" value={fmtINR(kpis?.total_receivable ?? 0)} testId="kpi-receivable" isMobile={isMobile} />
          <Kpi
            label="Overdue"
            value={fmtINR(kpis?.overdue_total ?? 0)}
            hint={kpis?.overdue_count ? `${kpis.overdue_count} project${kpis.overdue_count > 1 ? "s" : ""}` : "0 projects"}
            danger={kpis?.overdue_count > 0}
            testId="kpi-overdue"
            isMobile={isMobile}
          />
          <div style={{ gridColumn: isMobile ? "1 / -1" : undefined }}>
            <Kpi
              label="Cash collected (this month)"
              value={fmtINR(kpis?.cash_this_month ?? 0)}
              valueColor="#3F6B51"
              testId="kpi-cash-month"
              isMobile={isMobile}
            />
          </div>
        </div>

        {/* Tabs + filter row. Mobile: tabs render as horizontally-scrollable
            pill row (same treatment as Delivery filter pills); search + status
            filter button below. Desktop: original inline layout. */}
        {isMobile ? (
          <>
            <div
              data-testid="accounts-tabs"
              style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                marginBottom: 12,
                paddingBottom: 4,
              }}
            >
              <style>{`[data-testid="accounts-tabs"]::-webkit-scrollbar { display: none; }`}</style>
              {TABS.map((t) => {
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`accounts-tab-${t.key}`}
                    style={{
                      padding: "9px 15px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      background: active ? "#22382C" : "#FFFFFF",
                      color: active ? "#F4EFE4" : "#22382C",
                      border: `1px solid ${active ? "#22382C" : "#E4DCCD"}`,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {tab !== "recent" && tab !== "expenses" && tab !== "overhead" && (
              <div className="flex items-center gap-2 mb-4">
                <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                  <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A39A8B", pointerEvents: "none" }} />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search client name…"
                    data-testid="accounts-search"
                    style={{
                      width: "100%",
                      paddingLeft: 34, paddingRight: 12,
                      paddingTop: 11, paddingBottom: 11,
                      fontSize: 13.5,
                      background: "#FCFAF5",
                      border: "1px solid #E4DCCD",
                      borderRadius: 12,
                      color: "#2A2722",
                      outline: "none",
                    }}
                  />
                </div>
                <button
                  onClick={() => setStatusSheetOpen(true)}
                  data-testid="accounts-status-filter-btn"
                  aria-label="Filter by status"
                  style={{
                    background: statusFilter ? "#22382C" : "#FFFDFA",
                    color: statusFilter ? "#F4EFE4" : "#22382C",
                    border: `1px solid ${statusFilter ? "#22382C" : "#E4DCCD"}`,
                    borderRadius: 12,
                    padding: "11px 12px",
                    cursor: "pointer",
                    display: "grid", placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <SlidersHorizontal size={16} />
                </button>
              </div>
            )}
          </>
        ) : (
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E9E2D6",
            borderRadius: 12,
            padding: "0 24px",
            marginBottom: 18,
            boxShadow: "0 2px 6px rgba(60,46,20,.05)",
          }}
        >
          <div
            data-testid="accounts-tabs"
            style={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #EDE5D8",
              flexWrap: "wrap",
            }}
          >
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`accounts-tab-${t.key}`}
                  style={{
                    padding: "16px 18px",
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: active ? "#22382C" : "#76705F",
                    borderBottom: active ? "2.5px solid #22382C" : "2.5px solid transparent",
                    fontWeight: active ? 600 : 500,
                    marginBottom: -1,
                    transition: "color .15s, border-bottom-color .15s",
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Filter row */}
          {tab !== "recent" && tab !== "expenses" && tab !== "overhead" && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 0", flexWrap: "wrap" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#A39A8B",
                    pointerEvents: "none",
                  }}
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search client name…"
                  data-testid="accounts-search"
                  style={{
                    paddingLeft: 34,
                    paddingRight: 12,
                    paddingTop: 9,
                    paddingBottom: 9,
                    fontSize: 13,
                    background: "#FCFAF5",
                    border: "1px solid #E4DCCD",
                    borderRadius: 8,
                    width: 260,
                    color: "#2A2722",
                    outline: "none",
                  }}
                />
              </div>
              {/* Native <select> preserved — no custom chevron added. */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="accounts-status-filter"
                style={{
                  padding: "9px 12px",
                  paddingRight: 32,
                  fontSize: 13,
                  background: "#FCFAF5",
                  border: "1px solid #E4DCCD",
                  borderRadius: 8,
                  color: "#22382C",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="">All statuses</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Shoot done">Shoot done</option>
                <option value="Delivered">Delivered</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          )}
        </div>
        )}

        {loading ? (
          <div className="kicker text-brand-muted" data-testid="accounts-loading">Loading</div>
        ) : tab === "recent" ? (
          <RecentPaymentsTable txns={recentTxns} projects={projects} onChanged={refresh} />
        ) : tab === "expenses" ? (
          <ProjectExpensesTab />
        ) : tab === "overhead" ? (
          <StudioOverheadTab />
        ) : (
          <ProjectsTable
            projects={projects}
            onRecord={(p) => setRecordTarget(p)}
            onOpen={(p) => setSlideOverId(p.id)}
            isMobile={isMobile}
          />
        )}

        {/* Mobile status filter bottom sheet */}
        {isMobile && statusSheetOpen && (
          <AccountsStatusSheet
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            onClose={() => setStatusSheetOpen(false)}
          />
        )}

        {recordTarget && (
          <RecordPaymentModal
            project={recordTarget}
            onClose={() => setRecordTarget(null)}
            onRecorded={onRecorded}
          />
        )}

        {slideOverId && (
          <ProjectPaymentSlideOver
            projectId={slideOverId}
            onClose={() => setSlideOverId(null)}
            onChanged={onSlideChanged}
            onRecord={(p) => { setSlideOverId(null); setRecordTarget(p); }}
            onOpenProject={(id) => navigate(`/projects/${id}`)}
          />
        )}
      </div>
    </Layout>
  );
}

function Kpi({ label, value, hint, danger, valueColor, testId, isMobile }) {
  const finalColor = valueColor || (danger ? "#A35C45" : "#22382C");
  return (
    <div
      data-testid={testId}
      style={{
        background: danger ? "#F8E9E3" : "#FFFFFF",
        border: danger ? "1px solid #E5C9C0" : "1px solid #E9E2D6",
        borderRadius: isMobile ? 14 : 12,
        padding: isMobile ? "13px 15px" : "26px 30px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: danger ? "#A35C45" : "#A39A8B",
          marginBottom: isMobile ? 6 : 12,
        }}
      >
        {label}
      </div>
      <div
        className="font-display"
        style={{
          fontSize: isMobile ? "clamp(17px, 5.2vw, 36px)" : 36,
          lineHeight: 1.1,
          color: finalColor,
          wordBreak: isMobile ? "break-word" : "normal",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: danger ? "#A35C45" : "#76705F", marginTop: 6 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function ProjectsTable({ projects, onRecord, onOpen, isMobile }) {
  if (projects.length === 0) {
    return (
      <div
        data-testid="accounts-empty"
        style={{
          background: "#FFFFFF",
          border: "1px solid #EDE5D8",
          borderRadius: 12,
          padding: 56,
          textAlign: "center",
          boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        }}
      >
        <p style={{ fontSize: 14, fontStyle: "italic", color: "#76705F" }}>
          No active projects with payments to track.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="accounts-projects-table">
      {/* Column header row — desktop only */}
      {!isMobile && (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: ROW_GRID,
          gap: 16,
          alignItems: "center",
          padding: "0 24px",
          marginBottom: 12,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: "#A39A8B",
        }}
      >
        <div>Project</div>
        <div>Wedding</div>
        <div style={{ textAlign: "right" }}>Quote</div>
        <div style={{ textAlign: "right" }}>Collected</div>
        <div style={{ textAlign: "right" }}>Outstanding</div>
        <div>Next due</div>
        <div style={{ textAlign: "right" }}>Actions</div>
      </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 12 : 5 }}>
        {projects.map((p) => (
          isMobile ? (
            <ProjectRowMobile key={p.id} p={p} onRecord={onRecord} onOpen={onOpen} />
          ) : (
            <ProjectRow key={p.id} p={p} onRecord={onRecord} onOpen={onOpen} />
          )
        ))}
      </div>
    </div>
  );
}

function ProjectRow({ p, onRecord, onOpen }) {
  const s = p.payment_summary || {};
  const nd = s.next_due_installment;
  const displayName = p.client_name + (p.partner_name ? ` × ${p.partner_name}` : "");
  const dueLabel = nd ? (TITLE_LABEL[nd.label] || nd.label) : null;
  const dueDate = nd?.expected_due_date ? fmtDate(nd.expected_due_date) : null;
  const isOverdue = s.is_overdue;
  const outstanding = s.total_outstanding || 0;
  const outstandingColor = outstanding > 0 ? "#A35C45" : "#9A9387";

  return (
    <div
      onClick={() => onOpen(p)}
      data-testid={`accounts-row-${p.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: ROW_GRID,
        gap: 16,
        alignItems: "center",
        background: "#FFFFFF",
        border: "1px solid #EDE5D8",
        borderRadius: 12,
        padding: "7px 10px",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        cursor: "pointer",
        transition: "box-shadow .15s ease, transform .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 16px rgba(60,46,20,.12)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* PROJECT */}
      <div style={{ minWidth: 0 }} data-testid={`accounts-row-name-${p.id}`}>
        <div
          className="font-display"
          style={{ fontSize: 13, color: "#22382C", lineHeight: 1.2, wordBreak: "break-word" }}
        >
          {displayName}
        </div>
        <div style={{ fontSize: 10, color: "#76705F", marginTop: 1 }}>{p.status}</div>
      </div>

      {/* WEDDING */}
      <div style={{ fontSize: 12, color: "#2A2722" }}>
        {p.wedding_date ? fmtDate(p.wedding_date) : "—"}
      </div>

      {/* QUOTE */}
      <div style={{ textAlign: "right", fontSize: 12, fontWeight: 600, color: "#22382C", fontVariantNumeric: "tabular-nums" }}>
        {fmtINR(p.total_quote_value)}
      </div>

      {/* COLLECTED */}
      <div style={{ textAlign: "right", fontSize: 12, fontWeight: 600, color: "#3F6B51", fontVariantNumeric: "tabular-nums" }}>
        {fmtINR(s.total_collected)}
      </div>

      {/* OUTSTANDING */}
      <div
        data-testid={`accounts-outstanding-${p.id}`}
        style={{
          textAlign: "right",
          fontSize: 12,
          fontWeight: 600,
          color: outstandingColor,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmtINR(outstanding)}
      </div>

      {/* NEXT DUE */}
      <div style={{ minWidth: 0 }}>
        {dueLabel ? (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#22382C" }}>{dueLabel}</div>
            {dueDate && (
              <div style={{ fontSize: 10, color: "#76705F", marginTop: 1 }}>{dueDate}</div>
            )}
            {isOverdue && s.days_overdue > 0 && (
              <span
                data-testid={`accounts-overdue-badge-${p.id}`}
                style={{
                  display: "inline-block",
                  marginTop: 3,
                  background: "#FBEAE3",
                  color: "#A35C45",
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  padding: "2px 6px",
                  borderRadius: 6,
                }}
              >
                Overdue {s.days_overdue}d
              </span>
            )}
          </>
        ) : (
          <div style={{ fontSize: 11, color: "#76705F" }}>—</div>
        )}
      </div>

      {/* ACTIONS */}
      <div style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
        {outstanding > 0 ? (
          <button
            onClick={() => onRecord(p)}
            data-testid={`accounts-record-${p.id}`}
            style={{
              background: "#22382C",
              color: "#F4EFE4",
              border: "none",
              borderRadius: 6,
              padding: "5px 9px",
              minHeight: 26,
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "background-color .15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#152823")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#22382C")}
          >
            Record payment
          </button>
        ) : (
          <span
            style={{
              background: "#E4EDDF",
              color: "#3F6B51",
              borderRadius: 6,
              padding: "4px 8px",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              display: "inline-block",
            }}
          >
            Fully paid ✓
          </span>
        )}
      </div>
    </div>
  );
}

function RecentPaymentsTable({ txns, projects }) {
  const projectMap = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.id] = p;
    return m;
  }, [projects]);

  if (!txns || txns.length === 0) {
    return (
      <div
        data-testid="accounts-recent-empty"
        style={{
          background: "#FFFFFF",
          border: "1px solid #EDE5D8",
          borderRadius: 12,
          padding: 56,
          textAlign: "center",
          boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        }}
      >
        <p style={{ fontSize: 14, fontStyle: "italic", color: "#76705F" }}>No payments recorded yet.</p>
      </div>
    );
  }
  return (
    <div
      data-testid="accounts-recent-table"
      style={{
        background: "#FFFFFF",
        border: "1px solid #E9E2D6",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
      }}
    >
      <table className="w-full" style={{ fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#FAF6EC", borderBottom: "1px solid #EDE5D8" }}>
            <th style={{ textAlign: "left", padding: "14px 18px", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "#A39A8B" }}>Received on</th>
            <th style={{ textAlign: "left", padding: "14px 18px", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "#A39A8B" }}>Project</th>
            <th style={{ textAlign: "left", padding: "14px 18px", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "#A39A8B" }}>Installment · Method</th>
            <th style={{ textAlign: "left", padding: "14px 18px", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "#A39A8B" }}>Reference</th>
            <th style={{ textAlign: "right", padding: "14px 18px", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "#A39A8B" }}>Amount</th>
            <th style={{ textAlign: "left", padding: "14px 18px", fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "#A39A8B" }}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {txns.map((t) => {
            const proj = projectMap[t.project_id];
            const name = proj
              ? proj.client_name + (proj.partner_name ? ` × ${proj.partner_name}` : "")
              : t.project_id;
            const voided = !!t.voided;
            return (
              <tr key={t.transaction_id} style={{ borderBottom: "1px solid #F0EAE0" }} data-testid={`recent-txn-${t.transaction_id}`}>
                <td style={{ padding: "14px 18px", color: "#76705F" }}>{fmtDate(t.received_on)}</td>
                <td style={{
                  padding: "14px 18px",
                  textDecoration: voided ? "line-through" : "none",
                  color: voided ? "#A39A8B" : "#22382C",
                  fontWeight: 500,
                }}>{name}</td>
                <td style={{ padding: "14px 18px", color: "#22382C" }}>
                  {TITLE_LABEL[t.installment_label] || t.installment_label} · {t.method}
                  {voided && (
                    <span style={{
                      marginLeft: 8,
                      padding: "2px 8px",
                      fontSize: 9,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      background: "#FBEAE3",
                      color: "#A35C45",
                      borderRadius: 4,
                      fontWeight: 700,
                    }}>
                      VOIDED
                    </span>
                  )}
                </td>
                <td style={{ padding: "14px 18px", fontSize: 12, color: "#76705F" }}>{t.reference || "—"}</td>
                <td style={{
                  padding: "14px 18px",
                  textAlign: "right",
                  fontWeight: 600,
                  textDecoration: voided ? "line-through" : "none",
                  color: voided ? "#A39A8B" : (t.type === "refund_issued" ? "#A35C45" : "#3F6B51"),
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {t.type === "refund_issued" ? "− " : ""}{fmtINR(t.amount)}
                </td>
                <td style={{ padding: "14px 18px", fontSize: 12, color: "#76705F" }}>{t.notes || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 7.1 Pass 2D — Accounts mobile helpers.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mobile stacked project card (Accounts list, Screen 2D):
 *   Row 1 — project name (Newsreader) + wedding date + status subtext
 *   Row 2 — 3-col mini stats: Quote / Collected (green) / Outstanding (red|muted)
 *   Row 3 — Next Due: type + date, OVERDUE chip when applicable (omitted if fully paid)
 *   Row 4 — Full-width "Record Payment" button (dark) OR "✓ FULLY PAID" green pill
 *
 * Uses the SAME `onRecord` and `onOpen` handlers as the desktop row; the
 * displayName follows the same construction as the desktop row (i.e. the
 * known "× Sandeep" title-duplication artifact renders as-is — spec: "do
 * not sanitize"). Live data source only; the design mockup's fully-paid /
 * overdue swap is illustrative only.
 */
function ProjectRowMobile({ p, onRecord, onOpen }) {
  const s = p.payment_summary || {};
  const nd = s.next_due_installment;
  const displayName = p.client_name + (p.partner_name ? ` × ${p.partner_name}` : "");
  const dueLabel = nd ? (TITLE_LABEL[nd.label] || nd.label) : null;
  const dueDate = nd?.expected_due_date ? fmtDate(nd.expected_due_date) : null;
  const isOverdue = s.is_overdue;
  const outstanding = s.total_outstanding || 0;
  const fullyPaid = outstanding === 0 && (s.total_collected || 0) > 0;
  return (
    <div
      onClick={() => onOpen(p)}
      data-testid={`accounts-row-${p.id}`}
      style={{
        background: "#FFFDFA",
        borderRadius: 16,
        boxShadow: "0 4px 14px rgba(34,56,44,0.055), 0 1px 2px rgba(34,56,44,0.04)",
        padding: 14,
        cursor: "pointer",
      }}
    >
      {/* Row 1: name + wedding date */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
        <div
          className="font-display"
          data-testid={`accounts-row-name-${p.id}`}
          style={{
            fontSize: 16,
            color: "#22382C",
            lineHeight: 1.2,
            flex: 1,
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          {displayName}
        </div>
        <span
          style={{
            fontSize: 12,
            color: "#76705F",
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
            paddingTop: 2,
          }}
        >
          {p.wedding_date ? fmtDate(p.wedding_date) : "—"}
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: "#76705F", marginBottom: 12 }}>{p.status}</div>

      {/* Row 2: 3-col mini stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: nd || fullyPaid ? 10 : 12 }}>
        <MiniStat label="QUOTE" value={fmtINR(p.total_quote_value)} />
        <MiniStat label="COLLECTED" value={fmtINR(s.total_collected)} color="#3F6B51" />
        <MiniStat
          label="OUTSTANDING"
          value={fmtINR(outstanding)}
          color={outstanding > 0 ? "#A35C45" : "#9A9387"}
          testId={`accounts-outstanding-${p.id}`}
        />
      </div>

      {/* Row 3: next due (only if there's an outstanding installment) */}
      {dueLabel && !fullyPaid && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "9px 12px",
            background: "#FCFAF5",
            border: "1px solid #EDE5D8",
            borderRadius: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.13em", color: "#A39A8B", textTransform: "uppercase", marginBottom: 2 }}>
              Next due
            </div>
            <div style={{ fontSize: 12.5, color: "#22382C" }}>
              <span style={{ fontWeight: 600 }}>{dueLabel}</span>
              {dueDate && <span style={{ color: "#76705F", marginLeft: 6 }}>· {dueDate}</span>}
            </div>
          </div>
          {isOverdue && s.days_overdue > 0 && (
            <span
              data-testid={`accounts-overdue-badge-${p.id}`}
              style={{
                background: "#F2DBD3",
                color: "#A35C45",
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                padding: "3px 8px",
                borderRadius: 6,
                flexShrink: 0,
              }}
            >
              {s.days_overdue}d overdue
            </span>
          )}
        </div>
      )}

      {/* Row 4: action */}
      {fullyPaid ? (
        <div
          data-testid={`accounts-fully-paid-${p.id}`}
          style={{
            width: "100%",
            padding: "10px 14px",
            background: "#DDE9DE",
            color: "#3F6B51",
            border: "1px solid #C7DACB",
            borderRadius: 10,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          ✓ Fully Paid
        </div>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); onRecord(p); }}
          data-testid={`accounts-record-btn-${p.id}`}
          style={{
            width: "100%",
            background: "#22382C",
            color: "#F4EFE4",
            border: "none",
            borderRadius: 10,
            padding: "11px 14px",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(27,38,31,0.22)",
          }}
        >
          Record Payment
        </button>
      )}
    </div>
  );
}

/** Small labeled cell used inside the mobile ProjectRow's 3-col mini-stat strip. */
function MiniStat({ label, value, color, testId }) {
  return (
    <div data-testid={testId} style={{ minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: "#A39A8B", marginBottom: 3 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--ds-num-font)",
          fontSize: 13,
          fontWeight: 500,
          color: color || "#22382C",
          fontVariantNumeric: "tabular-nums",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
    </div>
  );
}

/** Mobile bottom-sheet for the status filter — same options as the desktop <select>. */
function AccountsStatusSheet({ statusFilter, setStatusFilter, onClose }) {
  const OPTIONS = [
    { v: "", l: "All statuses" },
    { v: "Confirmed", l: "Confirmed" },
    { v: "Shoot done", l: "Shoot done" },
    { v: "Delivered", l: "Delivered" },
    { v: "Completed", l: "Completed" },
  ];
  return (
    <>
      <div
        onClick={onClose}
        data-testid="accounts-status-scrim"
        style={{
          position: "fixed", inset: 0, zIndex: 10050,
          background: "rgba(20,30,24,0.42)",
          backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="accounts-status-sheet"
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          zIndex: 10051,
          background: "#FFFDFA",
          borderTopLeftRadius: 24, borderTopRightRadius: 24,
          boxShadow: "0 -12px 40px rgba(20,30,24,0.28)",
          padding: `18px 20px calc(env(safe-area-inset-bottom, 0px) + 18px)`,
          isolation: "isolate",
        }}
      >
        <div aria-hidden="true" style={{ width: 44, height: 5, background: "rgba(34,56,44,0.18)", borderRadius: 999, margin: "0 auto 14px" }} />
        <div style={{ fontFamily: "var(--ds-num-font)", fontSize: 20, color: "#22382C", marginBottom: 14 }}>
          Filter by status
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {OPTIONS.map((o) => {
            const active = statusFilter === o.v;
            return (
              <button
                key={o.v || "__all"}
                onClick={() => { setStatusFilter(o.v); onClose(); }}
                data-testid={`accounts-status-opt-${o.v || "all"}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "13px 14px",
                  background: active ? "#F1EDE2" : "#FCFAF5",
                  border: `1px solid ${active ? "#22382C" : "#E4DCCD"}`,
                  borderRadius: 12,
                  fontSize: 14,
                  color: "#22382C",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span>{o.l}</span>
                {active && <span style={{ color: "#22382C", fontWeight: 700 }}>✓</span>}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

